import { ResourceRequest } from '../../hooks/incidentResourceRequestHooks';
import { KanbanCard } from '../../../DesignSystems/Kanban';

interface NeedByKind {
  kind: string;
  type: string;
  totalRequired: number;
  totalAvailable: number;
  requirements: any[];
}

export const calculateAggregatedNeeds = (
  requirements: any[] | undefined,
  resourceSets: any[] | undefined,
  resourceRequests: ResourceRequest[] | undefined,
  selectedResourceKind: string
) => {
  if (!requirements || !resourceSets) return [];

  // Group requirements by kind
  const needsByKind: Record<string, NeedByKind> = {};

  // Aggregate requirements
  requirements
    .filter((req) => !req.archived)
    .filter(
      (req) =>
        selectedResourceKind === 'all' || req.kind === selectedResourceKind
    )
    .forEach((req) => {
      if (!needsByKind[req.kind]) {
        needsByKind[req.kind] = {
          kind: req.kind,
          type: req.type || 'Other',
          totalRequired: 0,
          totalAvailable: 0,
          requirements: [],
        };
      }
      needsByKind[req.kind].totalRequired += req.required_quantity;
      needsByKind[req.kind].requirements.push(req);
    });

  // Calculate available from resource sets
  resourceSets
    .filter((set) => !set.archived)
    .filter(
      (set) =>
        selectedResourceKind === 'all' || set.kind === selectedResourceKind
    )
    .forEach((set) => {
      if (needsByKind[set.kind]) {
        needsByKind[set.kind].totalAvailable += set.quantity;
      }
    });

  // Account for pending/approved requests (but NOT completed or cancelled ones)
  if (resourceRequests) {
    resourceRequests.forEach((request) => {
      if (request.status !== 'cancelled' && request.items) {
        // Skip if this request is completed (or effectively completed)
        const isCompleted =
          request.status === 'completed' ||
          (request.status === 'awaiting_payment' && request.finance_signature);

        if (!isCompleted) {
          request.items.forEach((item) => {
            if (
              item.kind &&
              needsByKind[item.kind] &&
              ['pending', 'approved', 'in progress'].includes(
                item.status?.toLowerCase()
              ) &&
              (selectedResourceKind === 'all' ||
                item.kind === selectedResourceKind)
            ) {
              needsByKind[item.kind].totalAvailable += item.quantity;
            }
          });
        }
      }
    });
  }

  // Convert to array and filter only positive needs
  return Object.values(needsByKind)
    .map((need) => ({
      ...need,
      needed: Math.max(need.totalRequired - need.totalAvailable, 0),
    }))
    .filter((need) => need.needed > 0);
};

export const calculateAvailableResourceKinds = (
  requirements: any[] | undefined,
  resourceSets: any[] | undefined,
  resourceRequests: ResourceRequest[] | undefined
): string[] => {
  const kinds = new Set<string>();

  // Add kinds from requirements that have positive needs
  if (requirements && resourceSets) {
    const needsByKind: Record<string, { required: number; available: number }> =
      {};

    // Calculate requirements
    requirements
      .filter((req) => !req.archived)
      .forEach((req) => {
        if (!needsByKind[req.kind]) {
          needsByKind[req.kind] = { required: 0, available: 0 };
        }
        needsByKind[req.kind].required += req.required_quantity;
      });

    // Calculate available
    resourceSets
      .filter((set) => !set.archived)
      .forEach((set) => {
        if (needsByKind[set.kind]) {
          needsByKind[set.kind].available += set.quantity;
        }
      });

    // Add kinds that have positive needs
    Object.entries(needsByKind).forEach(([kind, amounts]) => {
      if (amounts.required > amounts.available) {
        kinds.add(kind);
      }
    });
  }

  // Add kinds from resource requests (all statuses except cancelled)
  resourceRequests?.forEach((request) => {
    if (request.status !== 'cancelled' && request.items) {
      request.items.forEach((item) => {
        if (item.kind) {
          kinds.add(item.kind);
        }
      });
    }
  });

  return Array.from(kinds).sort();
};

export const getApprovalProgress = (request: any) => {
  const steps = [
    { name: 'Requestor', field: 'requestor_signature' },
    { name: 'Section Chief', field: 'section_chief_signature' },
    { name: 'RESL Review', field: 'resl_signature' },
    { name: 'Logistics', field: 'logistics_signature' },
    { name: 'Finance', field: 'finance_signature' },
  ];

  let completedSteps = 0;
  let currentStep = '';

  for (let i = 0; i < steps.length; i++) {
    if (request && request[steps[i].field]) {
      completedSteps++;
    } else {
      currentStep = steps[i].name;
      break;
    }
  }

  return {
    currentStep:
      completedSteps < steps.length
        ? `Step ${completedSteps + 1} of ${steps.length}: ${currentStep}`
        : '✓ Complete',
    progress: (completedSteps / steps.length) * 100,
  };
};

export const filterRequestsByKind = (
  requests: ResourceRequest[],
  selectedResourceKind: string
): ResourceRequest[] => {
  if (selectedResourceKind === 'all') return requests;

  return requests.filter((request) =>
    request.items?.some((item) => item.kind === selectedResourceKind)
  );
};

export const createRequestCard = (
  request: ResourceRequest,
  cardType: 'request' | 'approved' | 'cancelled'
): KanbanCard => {
  const baseCard = {
    id: request.id,
    title: request.name || 'Resource Request',
    description: `Request #: ${request.request_number}`,
    metadata: {
      ...request,
      cardType,
    },
  };

  if (cardType === 'request') {
    return {
      ...baseCard,
      metadata: {
        ...baseCard.metadata,
        approval: getApprovalProgress(request),
        // Include first few items for display
        items: request.items?.slice(0, 3),
        totalItemCount: request.items?.length || 0,
      },
    };
  } else if (cardType === 'approved') {
    return {
      ...baseCard,
      metadata: {
        ...baseCard.metadata,
        // Include ALL items for display
        items: request.items,
        totalItemCount: request.items?.length || 0,
      },
    };
  }

  return baseCard;
};
